import { useState } from "react";
import { Flag, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const PHOTO_REASONS = [
  { key: "minor", label: "Child safety / Underage user", critical: true },
  { key: "nudity", label: "Nudity or sexual content" },
  { key: "violence", label: "Violence or dangerous content" },
  { key: "harassment", label: "Harassment or bullying" },
  { key: "stigma", label: "Stigma or discrimination" },
  { key: "racist", label: "Racist content" },
  { key: "spam", label: "Spam or scam" },
  { key: "inappropriate", label: "Inappropriate content" },
  { key: "other", label: "Other" },
];

const STORY_REASONS = [
  { key: "minor", label: "Child safety / Underage user", critical: true },
  { key: "racist", label: "Racist content" },
  { key: "stigma", label: "Stigma or discrimination" },
  { key: "sexual", label: "Sexually inappropriate" },
  { key: "harassment", label: "Harassment or bullying" },
  { key: "inappropriate", label: "Inappropriate content" },
  { key: "other", label: "Other" },
];

export default function ReportButton({
  type = "photo",
  reportedUserId,
  imageIndex = null,
  storyId = null,
  onReported,
}) {
  const [open, setOpen] = useState(false);

  const reasons = type === "story" ? STORY_REASONS : PHOTO_REASONS;

  const handleReport = async (reason) => {
    try {
      await api.post("/report-content", {
        reported_user_id: reportedUserId,
        reason: reason,
        image_index: type === "photo" ? (imageIndex ?? 0) : null,
        story_id: type === "story" ? storyId : null,
      });
      toast.success("Reported. Our team will review it.");
      setOpen(false);
      onReported?.();
    } catch (err) {
      toast.error("Failed to report. Try again.");
    }
  };

  return (
    <>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        className="p-1.5 rounded-full bg-black/40 hover:bg-black/60 text-white transition-colors"
        title="Report this content"
      >
        <Flag className="w-4 h-4" />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="bg-white rounded-2xl max-w-sm w-full p-5 animate-fade-in max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-semibold text-lg mb-3">
              Report {type === "story" ? "Story" : "Photo"}
            </h3>

            <div className="p-3 mb-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <ShieldAlert className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-red-800 leading-relaxed">
                Haven has a <strong>zero-tolerance policy</strong> for child sexual abuse and
                exploitation. Reports are reviewed immediately.
              </p>
            </div>

            <p className="text-sm text-gray-500 mb-3">Select a reason:</p>
            <div className="space-y-1.5">
              {reasons.map((r) => (
                <button
                  key={r.key}
                  onClick={() => handleReport(r.key)}
                  className={`w-full text-left px-4 py-2.5 rounded-lg border text-sm transition-colors ${
                    r.critical
                      ? "border-red-300 bg-red-50 text-red-700 font-bold hover:bg-red-100"
                      : "border-gray-200 font-medium text-gray-800 hover:bg-red-50"
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>

            <button
              onClick={() => setOpen(false)}
              className="mt-4 w-full text-sm text-gray-500 hover:text-gray-700 transition-colors py-2"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </>
  );
}